/**
 * SuggestedActionCard Component
 *
 * Displays a single PTO suggestion with:
 * - Date range and status label
 * - PTO summary (days used vs days of rest)
 * - Action details (team conflicts, energy factors)
 * - Action buttons (request dates, secondary action)
 * - Mini calendar visualization
 *
 * This component is responsive and adapts layout for screens < 968px
 */

import React from 'react'
import { Card } from 'antd'
import { PTOSuggestion } from '../../types'
import { MiniCalendar } from './mini-calendar'
import { useCalendarDays } from './hooks/useCalendarDays'
import { CardHeader } from './components/card-header'
import { PTOSummary } from './components/pto-summary'
import { ActionDetails } from './components/action-details'
import { ActionButtons } from './components/action-buttons'
import { StatusLabelType } from './utils/statusLabel'
import './suggested-action-card.less'

export interface SuggestedActionCardProps {
  /** The PTO suggestion to display */
  suggestion: PTOSuggestion
  /** Index of the suggestion in the list */
  index: number
  /** Whether this is the best option (affects styling) */
  isBestOption: boolean
  /** Status label type to display */
  statusLabelType: StatusLabelType
  /** Week start preference: 0 = Sunday, 1 = Monday */
  weekStartsOn?: 0 | 1
  /** Array of working day numbers (0=Sunday, 1=Monday, ..., 6=Saturday) */
  workWeekDays?: number[]
  nonWorkingDays?: string[]
  /** Callback when "Request These Dates" button is clicked */
  onRequestClick?: (suggestion: PTOSuggestion, index: number, isBestOption: boolean) => void
}

/**
 * SuggestedActionCard displays a single PTO suggestion with calendar visualization
 *
 * @example
 * <SuggestedActionCard
 *   suggestion={suggestion}
 *   index={0}
 *   isBestOption={true}
 *   statusLabel="BEST OPTION"
 *   weekStartsOn={1}
 *   workWeekDays={[1, 2, 3, 4, 5]}
 *   onRequestClick={handleRequest}
 * />
 */
const SuggestedActionCard: React.FC<SuggestedActionCardProps> = ({
  suggestion,
  index,
  isBestOption,
  statusLabelType,
  weekStartsOn = 1,
  workWeekDays,
  nonWorkingDays,
  onRequestClick,
}) => {
  // Calculate calendar days with types using custom hook
  const calendarDays = useCalendarDays({
    suggestion,
    weekStartsOn,
    workWeekDays,
    nonWorkingDays,
  })

  // Wrap onRequestClick to match expected signature
  const handleRequestClick = (suggestion: PTOSuggestion) => {
    if (onRequestClick) {
      onRequestClick(suggestion, index, isBestOption)
    }
  }

  return (
    <Card
      className={`suggested-action-card ${isBestOption ? 'best-option' : ''}`}
    >
      <div className="suggested-action-card-wrapper">
        <div className="suggested-action-card-content">
          <CardHeader
            startDate={suggestion.startDate}
            endDate={suggestion.endDate}
            statusLabelType={statusLabelType}
            isBestOption={isBestOption}
          />

          <PTOSummary
            ptoDaysUsed={suggestion.ptoDaysUsed}
            durationDays={suggestion.durationDays}
          />

          <ActionDetails suggestion={suggestion} />

          <ActionButtons
            suggestion={suggestion}
            onRequestClick={handleRequestClick}
          />
        </div>

        <div className="suggested-action-calendar">
          <MiniCalendar days={calendarDays} weekStartsOn={weekStartsOn} />
        </div>
      </div>
    </Card>
  )
}

export default SuggestedActionCard
